import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  App,
  Button,
  Card,
  FloatButton,
  Form,
  Input,
  Popconfirm,
  Result,
  Space,
  Spin,
  Tabs,
  Typography,
} from 'antd';
import { MessageOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { AnalyticsTab } from '@/features/analytics/ui/AnalyticsTab';
import { AssistantDrawer } from '@/features/assistant/ui/AssistantDrawer';
import { CampaignsTab } from '@/features/campaigns/ui/CampaignsTab';
import { CatalogueTab } from '@/features/catalogue/ui/CatalogueTab';
import { ContentTab } from '@/features/content/ui/ContentTab';
import { VideoTab } from '@/features/video/ui/VideoTab';
import { MovieReviewTab } from '@/features/moviereviews/ui/MovieReviewTab';
import { IntegrationsTab } from '@/features/integrations/ui/IntegrationsTab';
import { useActiveOrg } from '@/features/organizations/active-org-context';
import { projectsApi } from '../projects.api';
import { BrandForm } from './BrandForm';
import { ProjectMembersTab } from './ProjectMembersTab';

const nameSchema = z.object({ name: z.string().trim().min(2).max(80) });
type NameForm = z.infer<typeof nameSchema>;

export function ProjectDetailPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const projectId = useParams({ strict: false }).projectId ?? '';
  const { activeOrg, activeOrgId } = useActiveOrg();
  const [assistantOpen, setAssistantOpen] = useState(false);

  const search = new URLSearchParams(window.location.search);
  const initialTab = search.get('tab') ?? 'overview';
  const connected = search.get('connected');

  useEffect(() => {
    if (connected) message.success(`Connected ${connected}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const role = activeOrg?.role;
  const canEdit = role === 'owner' || role === 'admin';
  const isOwner = role === 'owner';

  const projectKey = ['project', activeOrgId, projectId] as const;
  const query = useQuery({
    queryKey: projectKey,
    queryFn: () => projectsApi.get(activeOrgId ?? '', projectId),
    enabled: Boolean(activeOrgId),
    retry: false,
  });

  const nameForm = useForm<NameForm>({
    resolver: zodResolver(nameSchema),
    values: { name: query.data?.name ?? '' },
  });
  const renameMutation = useMutation({
    mutationFn: (v: NameForm) => projectsApi.update(activeOrgId ?? '', projectId, v),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: projectKey });
      await queryClient.invalidateQueries({ queryKey: ['projects', activeOrgId] });
      message.success('Project updated');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to update'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => projectsApi.remove(activeOrgId ?? '', projectId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['projects', activeOrgId] });
      message.success('Project deleted');
      void navigate({ to: '/projects' });
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to delete'),
  });

  if (!activeOrgId)
    return <Typography.Paragraph>Select an organization first.</Typography.Paragraph>;
  if (query.isLoading) return <Spin />;
  if (query.isError || !query.data) {
    return (
      <Result status="404" title="Project not found" subTitle="You may not have access to it." />
    );
  }

  const project = query.data;

  const overview = (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card size="small">
        <Form layout="inline" onFinish={nameForm.handleSubmit((v) => renameMutation.mutate(v))}>
          <Controller
            control={nameForm.control}
            name="name"
            render={({ field, fieldState }) => (
              <Form.Item
                label="Name"
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
              >
                <Input {...field} disabled={!canEdit} style={{ width: 260 }} />
              </Form.Item>
            )}
          />
          {canEdit ? (
            <Button type="primary" htmlType="submit" loading={renameMutation.isPending}>
              Save
            </Button>
          ) : null}
        </Form>
        <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
          Slug: {project.slug}
        </Typography.Paragraph>
      </Card>
      {isOwner ? (
        <Card size="small" title="Danger zone">
          <Popconfirm
            title="Delete this project and all its data?"
            onConfirm={() => deleteMutation.mutate()}
          >
            <Button danger loading={deleteMutation.isPending}>
              Delete project
            </Button>
          </Popconfirm>
        </Card>
      ) : null}
    </Space>
  );

  const isVideo = project.businessModel === 'faceless_video';
  const isMovieReview = project.businessModel === 'movie_review';
  const isDidYouKnow = project.businessModel === 'did_you_know';
  const items = [
    { key: 'overview', label: 'Overview', children: overview },
    {
      key: 'brand',
      label: 'Brand',
      children: <BrandForm orgId={activeOrgId} project={project} canEdit={canEdit} />,
    },
    // Video projects get just their studio; marketing keeps the full suite.
    ...(isMovieReview
      ? [
          {
            key: 'reviews',
            label: 'Movie reviews',
            children: (
              <MovieReviewTab orgId={activeOrgId} projectId={projectId} canEdit={canEdit} />
            ),
          },
        ]
      : isVideo || isDidYouKnow
        ? [
            {
              key: 'videos',
              label: isDidYouKnow ? 'Did you know' : 'Videos',
              children: (
                <VideoTab
                  orgId={activeOrgId}
                  projectId={projectId}
                  canEdit={canEdit}
                  {...(isDidYouKnow ? { lockedVisualMode: 'stock_loop' as const } : {})}
                />
              ),
            },
          ]
        : [
            {
              key: 'catalogue',
              label: 'Catalogue',
              children: (
                <CatalogueTab orgId={activeOrgId} projectId={projectId} canEdit={canEdit} />
              ),
            },
            {
              key: 'content',
              label: 'Content',
              children: <ContentTab orgId={activeOrgId} projectId={projectId} canEdit={canEdit} />,
            },
            {
              key: 'analytics',
              label: 'Analytics',
              children: (
                <AnalyticsTab orgId={activeOrgId} projectId={projectId} canEdit={canEdit} />
              ),
            },
            {
              key: 'campaigns',
              label: 'Campaigns',
              children: (
                <CampaignsTab orgId={activeOrgId} projectId={projectId} canEdit={canEdit} />
              ),
            },
          ]),
    {
      key: 'integrations',
      label: 'Integrations',
      children: <IntegrationsTab orgId={activeOrgId} projectId={projectId} canEdit={canEdit} />,
    },
    ...(canEdit
      ? [
          {
            key: 'members',
            label: 'Members',
            children: (
              <ProjectMembersTab orgId={activeOrgId} projectId={projectId} canManage={isOwner} />
            ),
          },
        ]
      : []),
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ marginBottom: 0 }}>
        {project.name}
      </Typography.Title>
      <Tabs
        defaultActiveKey={initialTab}
        items={items}
        onChange={(k) => {
          const url = new URL(window.location.href);
          url.searchParams.set('tab', k);
          url.searchParams.delete('connected');
          window.history.replaceState(null, '', url.toString());
        }}
      />
      {canEdit ? (
        <>
          <FloatButton
            icon={<MessageOutlined />}
            type="primary"
            tooltip="Ask the AI assistant"
            aria-label="Open AI assistant"
            onClick={() => setAssistantOpen(true)}
          />
          <AssistantDrawer
            orgId={activeOrgId}
            projectId={projectId}
            open={assistantOpen}
            onClose={() => setAssistantOpen(false)}
          />
        </>
      ) : null}
    </Space>
  );
}
